import React, { useEffect, useState } from "react"
import {
  AsyncTypeahead,
  Highlighter,
  Menu,
  MenuItem,
  TypeaheadInputMulti,
  Token as RBTToken,
} from "react-bootstrap-typeahead"
import { defaultFilterBy } from "react-bootstrap-typeahead/lib/utils"
import _ from "lodash"

import * as AsyncField from "@/helpers/AsyncField"

// For a picker inside a `scrollable` modal whose options are compared by eye
// (the import-source pickers, SHO-TSK-1518): a tall menu over the page rather
// than one clipped to the modal body's height.
export const TALL_OVERLAY_MENU = { menuPositionFixed: true, menuMaxHeight: "50vh" }

export const DataStates = {
  LOADING: "LOADING",
  ABSENT: "ABSENT",
  PRESENT: "PRESENT",
}

// Custom menu used when options carry a `group` caption: renders a
// non-selectable header whenever the group changes (options arrive already
// grouped from the server ranking). Mirrors the library's default menu for
// pagination rows.
const renderGroupedMenu = (results, menuProps, state) => {
  const { paginationText } = menuProps
  const rest = _.omit(menuProps, [
    "newSelectionPrefix",
    "paginationText",
    "renderMenuItemChildren",
    "labelKey",
    "text",
  ])

  let lastGroup = null

  const items = results.map((option, position) => {
    if (option.paginationOption) {
      return (
        <React.Fragment key="pagination-item">
          <Menu.Divider />
          <MenuItem
            option={option}
            position={position}
            className="rbt-menu-pagination-option"
            label={paginationText}
          >
            {paginationText}
          </MenuItem>
        </React.Fragment>
      )
    }

    const header =
      option.group && option.group !== lastGroup ? (
        <Menu.Header>
          <em>{option.group}:</em>
        </Menu.Header>
      ) : null
    lastGroup = option.group || lastGroup

    return (
      <React.Fragment key={option.label || position}>
        {header}
        <MenuItem option={option} position={position} label={option.label}>
          <Highlighter search={state.text}>{option.label}</Highlighter>
        </MenuItem>
      </React.Fragment>
    )
  })

  return <Menu {...rest}>{items}</Menu>
}

const Select = ({
  id,
  placeholder,
  absentDataMessage = "No options available.",
  options,
  selected,
  dataState,
  isLoading,
  onChange,
  onSearch,
  disabled = false,
  positionFixed = false,
  maxHeight = undefined,
}): JSX.Element => (
  <AsyncTypeahead
    id={id}
    positionFixed={positionFixed}
    maxHeight={maxHeight}
    multiple
    selected={selected}
    onChange={onChange}
    onSearch={onSearch}
    options={options}
    minLength={0}
    useCache
    isLoading={isLoading}
    renderMenu={_.some(options, "group") ? renderGroupedMenu : undefined}
    placeholder={
      dataState == DataStates.LOADING
        ? "Loading..."
        : dataState === DataStates.ABSENT
          ? absentDataMessage
          : placeholder
    }
    disabled={disabled}
    filterBy={(option, props) => {
      if (props.text.length === 0 && option.new) {
        return false
      }
      return defaultFilterBy(option, { ...props, filterBy: [] })
    }}
    renderInput={(inputProps, props) => (
      <TypeaheadInputMulti {...inputProps} selected={selected}>
        {selected.map((option, idx) => (
          <RBTToken index={idx} key={option.label} onRemove={props.onRemove} option={option}>
            {option.selectLabel || option.label}
          </RBTToken>
        ))}
      </TypeaheadInputMulti>
    )}
  />
)

const AsyncSearch = ({
  selectedValues,
  setSelectedValues,
  getOptions,
  id = "async-typeahead-select",
  onOptionsLoaded: setDataState = (_: any) => {},
  absentDataMessage = "No options available.",
  dataState = DataStates.LOADING,
  placeholder = "Choose an option",
  maxSelectable = null,
  // Inside a scrolling container (a `scrollable` modal body, say) the menu is
  // clipped to that container's height; fixed positioning lets it overlay
  // the page instead. maxHeight overrides the library's 300px.
  menuPositionFixed = false,
  menuMaxHeight = undefined,
}): JSX.Element => {
  const [optionsField, setOptionsField] = useState(AsyncField.createField({ defaultValue: [] }))

  const [options, setOptions] = useState([])
  const [initialized, setInitialized] = useState(false)

  const maxReached = maxSelectable ? selectedValues.length >= maxSelectable : false

  const searchOptions = (query) => {
    const field = AsyncField.cloneField(optionsField)

    setOptionsField(AsyncField.setFieldLoading(field))

    getOptions(query)
      .then((response) => {
        setOptionsField(AsyncField.setFieldReady(field, { value: response.data.data.options }))
        // Optional metadata for callers that surface truncation ("showing N
        // of M"); existing callers take a single argument and ignore this.
        const meta = {
          shown: response.data.data.options.length,
          totalCount: response.data.data.totalCount,
        }
        if (_.isEmpty(response.data.data.options)) {
          console.log("data absent")
          setDataState(DataStates.ABSENT, meta)
        } else {
          console.log("data present")
          setDataState(DataStates.PRESENT, meta)
        }

        if (_.find(field.value, { label: query })) {
          setOptions(field.value)
        } else {
          setOptions(
            _.concat(field.value, {
              new: true,
              label: `New Option: ${query}`,
              value: query,
              selectLabel: query,
            })
          )
        }
      })
      .catch(() => {
        setOptionsField(AsyncField.setFieldFailed(field))
      })
  }

  useEffect(() => {
    if (!initialized) {
      // preload options
      searchOptions("")
      setInitialized(true)
    }
  }, [initialized])

  return (
    <Select
      id={id}
      placeholder={placeholder}
      absentDataMessage={absentDataMessage}
      isLoading={AsyncField.isLoading(optionsField)}
      dataState={dataState}
      options={options}
      selected={selectedValues}
      onChange={setSelectedValues}
      onSearch={searchOptions}
      disabled={maxReached}
      positionFixed={menuPositionFixed}
      maxHeight={menuMaxHeight}
    />
  )
}

export default AsyncSearch
